import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { contactData } from '../data/mockData';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      toast({
        title: "Message Sent! 🎉",
        description: "Apun tumse jaldi contact karega! Check your email for confirmation.",
        duration: 5000,
      });
      
      // Reset form
      setFormData({ name: '', email: '', service: '', message: '', phone: '' });
      
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: "Error! 😕",
        description: "Kuch gadbad hai! Try again ya direct WhatsApp kar.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50 relative overflow-hidden" id="contact">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <Badge className="bg-red-100 text-red-800 border-red-200 mb-4 px-4 py-2">
              Contact
            </Badge>
            <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-6">
              {contactData.title} 📞
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              {contactData.subtitle}
            </p>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              {contactData.description}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              {/* Quick Connect Cards */}
              <div className="grid gap-6">
                <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                  <CardContent className="p-6">
                    <div className="flex items-center">
                      <div className="text-4xl mr-4">📱</div>
                      <div>
                        <h3 className="font-bold text-lg mb-2">WhatsApp pe Direct Message!</h3>
                        <p className="text-green-100 mb-3">Instant reply milega! Online hun 24/7 📲</p>
                        <Button 
                          className="bg-white text-green-600 hover:bg-gray-100 font-semibold"
                          onClick={() => window.open(contactData.socialLinks.whatsapp, '_blank')}
                        >
                          WhatsApp Now 💬
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-r from-purple-500 to-pink-600 text-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                  <CardContent className="p-6">
                    <div className="flex items-center">
                      <div className="text-4xl mr-4">📧</div>
                      <div>
                        <h3 className="font-bold text-lg mb-2">Email Bhi Kar Sakta Hai!</h3>
                        <p className="text-purple-100 mb-3">{contactData.email}</p>
                        <Button 
                          className="bg-white text-purple-600 hover:bg-gray-100 font-semibold"
                          onClick={() => window.open(contactData.socialLinks.email, '_blank')}
                        >
                          Send Email 📧
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-r from-pink-500 to-red-500 text-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                  <CardContent className="p-6">
                    <div className="flex items-center">
                      <div className="text-4xl mr-4">📸</div>
                      <div>
                        <h3 className="font-bold text-lg mb-2">Instagram pe Follow Kar!</h3>
                        <p className="text-pink-100 mb-3">{contactData.instagram} - Daily updates! 🔥</p>
                        <Button 
                          className="bg-white text-pink-600 hover:bg-gray-100 font-semibold"
                          onClick={() => window.open(contactData.socialLinks.instagram, '_blank')}
                        >
                          Follow Now 📸
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Working Hours */}
              <Card className="bg-white shadow-xl border-0">
                <CardHeader>
                  <CardTitle className="flex items-center text-gray-900">
                    <span className="text-2xl mr-3">⏰</span>
                    Working Hours
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Monday - Friday</span>
                    <Badge className="bg-green-100 text-green-800">9 AM - 10 PM</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Saturday</span>
                    <Badge className="bg-blue-100 text-blue-800">10 AM - 8 PM</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Sunday</span>
                    <Badge className="bg-orange-100 text-orange-800">11 AM - 6 PM</Badge>
                  </div>
                  <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <strong>Emergency Projects:</strong> 24/7 available for urgent work! 🚨
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <Card className="bg-white shadow-2xl border-0">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-gray-900 text-center">
                  Project ke liye Form Bhar! 📝
                </CardTitle>
                <p className="text-gray-600 text-center">
                  Details bata de, quote bhej dunga jaldi! Free consultation hai! 🆓
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">
                      Tera Naam Kya Hai? *
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Apna naam likh..."
                      required
                      className="border-2 border-gray-200 focus:border-blue-500 transition-colors duration-300"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">
                      Email Address *
                    </label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      required
                      className="border-2 border-gray-200 focus:border-blue-500 transition-colors duration-300"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">
                      Phone Number (Optional)
                    </label>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 98765 43210"
                      className="border-2 border-gray-200 focus:border-blue-500 transition-colors duration-300"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">
                      Kya Service Chahiye? *
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full p-3 border-2 border-gray-200 rounded-md focus:border-blue-500 transition-colors duration-300 bg-white"
                    >
                      <option value="">Select kar...</option>
                      <option value="logo-design">Logo Design</option>
                      <option value="website-development">Website Development</option>
                      <option value="pr-kit">PR Kit</option>
                      <option value="creative-videos">Creative Videos</option>
                      <option value="complete-branding">Complete Branding Package</option>
                      <option value="custom">Custom Requirement</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">
                      Project Details *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Bata de kya banana hai, budget kya hai, timeline kya chahiye... jo bhi mind mein hai likh de!"
                      required
                      rows={5}
                      className="border-2 border-gray-200 focus:border-blue-500 transition-colors duration-300 resize-none"
                    />
                  </div>

                  <Button 
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-4 text-lg transform hover:scale-105 transition-all duration-300 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {loading ? 'Sending... 📤' : 'Send Message 🚀'}
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    Response time: Usually within 2-3 hours! ⚡
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;